import { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { Key, Loader2, Clock, XCircle } from 'lucide-react';
import { intercomAction } from '@/lib/api';

interface GuestKeyData {
  qr_string: string;
  visitor_name: string;
  device_name: string;
  org_name: string;
  key_expires_at: string;
}

export default function GuestKeyPage() {
  const { token } = useParams<{ token: string }>();
  const [data, setData] = useState<GuestKeyData | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [timeLeft, setTimeLeft] = useState<number | null>(null);
  const [QRCode, setQRCode] = useState<any>(null);

  // Lazy-load QR code component
  useEffect(() => {
    import('qrcode.react').then((mod) => {
      setQRCode(() => mod.QRCodeSVG);
    });
  }, []);

  // Fetch guest key data
  useEffect(() => {
    if (!token) {
      setError('Ogiltig länk');
      setLoading(false);
      return;
    }

    async function fetchKey() {
      const result = await intercomAction<GuestKeyData>('get-guest-key', {
        access_token: token,
      });

      if (result.error || !result.data) {
        setError(result.error || 'Kunde inte hämta nyckel');
      } else {
        setData(result.data);
      }
      setLoading(false);
    }

    fetchKey();
  }, [token]);

  // Countdown timer
  useEffect(() => {
    if (!data?.key_expires_at) return;

    const updateTimeLeft = () => {
      const remaining = Math.max(
        0,
        Math.floor((new Date(data.key_expires_at).getTime() - Date.now()) / 1000)
      );
      setTimeLeft(remaining);

      if (remaining <= 0) {
        setError('Nyckeln har gått ut');
        setData(null);
      }
    };

    updateTimeLeft();
    const interval = setInterval(updateTimeLeft, 1000);
    return () => clearInterval(interval);
  }, [data?.key_expires_at]);

  const formatTime = (seconds: number) => {
    const h = Math.floor(seconds / 3600);
    const m = Math.floor((seconds % 3600) / 60);
    const s = seconds % 60;
    if (h > 0) return `${h}:${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
    return `${m}:${String(s).padStart(2, '0')}`;
  };

  return (
    <div className="flex min-h-screen flex-col items-center justify-center p-6 bg-background">
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-1/4 left-1/2 -translate-x-1/2 w-[500px] h-[500px] bg-gradient-to-br from-muted/30 to-transparent rounded-full blur-3xl" />
      </div>

      <div className="w-full max-w-sm space-y-6 relative z-10">
        {/* Loading */}
        {loading && (
          <div className="text-center space-y-4 py-12">
            <Loader2 className="h-10 w-10 animate-spin text-muted-foreground mx-auto" />
            <p className="text-sm text-muted-foreground">Hämtar nyckel...</p>
          </div>
        )}

        {/* Error / Expired */}
        {!loading && error && (
          <div className="text-center space-y-4">
            <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-2xl bg-card/80 backdrop-blur border border-border shadow-lg">
              <XCircle className="h-8 w-8 text-destructive" />
            </div>
            <div>
              <h1 className="text-xl font-bold">{error}</h1>
              <p className="text-sm text-muted-foreground mt-1">
                Kontakta personen som skickade nyckeln för att få en ny.
              </p>
            </div>
          </div>
        )}

        {/* QR Key display */}
        {!loading && data && (
          <div className="text-center space-y-6 animate-fade-in">
            {/* Header */}
            <div className="space-y-2">
              <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-2xl bg-card/80 backdrop-blur border border-border shadow-lg">
                <Key className="h-8 w-8 text-primary" />
              </div>
              <h1 className="text-2xl font-bold tracking-tight">
                {data.org_name || 'ZipLink'}
              </h1>
              <p className="text-sm text-muted-foreground">
                Nyckel till <span className="font-medium">{data.device_name}</span>
              </p>
              {data.visitor_name && (
                <p className="text-sm text-muted-foreground">
                  Hej {data.visitor_name}!
                </p>
              )}
            </div>

            {/* QR Code */}
            <div className="bg-white p-6 rounded-2xl inline-block mx-auto shadow-lg">
              {QRCode ? (
                <QRCode value={data.qr_string} size={220} level="M" />
              ) : (
                <div
                  style={{ width: 220, height: 220 }}
                  className="flex items-center justify-center"
                >
                  <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
                </div>
              )}
            </div>

            <p className="text-sm text-muted-foreground">
              Visa QR-koden för enhetens läsare
            </p>

            {/* Countdown */}
            {timeLeft !== null && timeLeft > 0 && (
              <div className="flex items-center justify-center gap-2 text-muted-foreground">
                <Clock className="h-4 w-4" />
                <span className="font-mono text-sm">
                  Giltig i {formatTime(timeLeft)}
                </span>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
